// src/components/common/StatusToggle.jsx
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Power, Loader2 } from 'lucide-react';

const StatusToggle = ({
  isOnline,
  onToggle,
  isLoading = false,
  todayDeliveries = 0
}) => {
  return (
    <div className="relative">
      {/* Main Card */}
      <motion.div
        className={`relative overflow-hidden rounded-2xl p-4 transition-all duration-500 ${
          isOnline
            ? 'bg-gradient-to-br from-emerald-500 to-emerald-700'
            : 'bg-gradient-to-br from-gray-600 to-gray-800'
        }`}
        layout
      >
        {/* Animated background pattern */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className={`absolute -top-1/2 -right-1/2 w-full h-full rounded-full ${
              isOnline ? 'bg-emerald-400/20' : 'bg-gray-500/20'
            }`}
            animate={{
              scale: isOnline ? [1, 1.2, 1] : 1,
              opacity: isOnline ? [0.2, 0.4, 0.2] : 0.1,
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </div>

        <div className="relative flex items-center justify-between gap-4">
          {/* Status Info */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <motion.div
                className={`w-3 h-3 rounded-full ${
                  isOnline ? 'bg-white' : 'bg-gray-400'
                }`}
                animate={{
                  scale: isOnline ? [1, 1.2, 1] : 1,
                  opacity: isOnline ? [1, 0.7, 1] : 0.5,
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />
              <span className="text-white font-semibold text-lg">
                {isOnline ? 'En ligne' : 'Hors ligne'}
              </span>
            </div>

            <AnimatePresence mode="wait">
              {isOnline && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="text-white/80 text-sm"
                >
                  Vous recevez vos livraisons
                </motion.p>
              )}
            </AnimatePresence>

            {/* Today's stats when online */}
            <AnimatePresence mode="wait">
              {isOnline && todayDeliveries > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex gap-4 mt-3 pt-3 border-t border-white/20"
                >
                  <div>
                    <p className="text-white/60 text-xs">Aujourd'hui</p>
                    <p className="text-white font-bold">{todayDeliveries} livraison{todayDeliveries > 1 ? 's' : ''}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Toggle Button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onToggle}
            disabled={isLoading}
            className={`relative w-16 h-16 rounded-2xl flex items-center justify-center transition-all ${
              isOnline
                ? 'bg-white text-emerald-600 shadow-lg shadow-emerald-900/30'
                : 'bg-gray-500 text-white hover:bg-gray-400'
            }`}
          >
            {isLoading ? (
              <Loader2 className="w-7 h-7 animate-spin" />
            ) : (
              <Power className="w-7 h-7" />
            )}

            {/* Ripple effect on click */}
            <motion.div
              className="absolute inset-0 rounded-2xl"
              initial={false}
              animate={isLoading ? { scale: [1, 1.1, 1], opacity: [0.5, 0, 0.5] } : {}}
              transition={{ duration: 1, repeat: Infinity }}
            />
          </motion.button>
        </div>
      </motion.div>

      {/* Help text */}
      <p className="text-center text-xs text-gray-500 mt-2">
        {isOnline
          ? 'Appuyez pour vous déconnecter'
          : 'Appuyez pour commencer à recevoir vos livraisons'}
      </p>
    </div>
  );
};

export default StatusToggle;
